import type { EntityType } from '@keel/core';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Layers, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { useSession } from '@/lib/session';
import { useAppStore } from '@/lib/store';
import { NewEntityDialog } from './NewEntityDialog';

export const ENTITY_TYPE_LABEL: Record<EntityType, string> = {
  individual: 'Personal', household: 'Household', smsf: 'SMSF', trust: 'Trust', company: 'Company',
  partnership: 'Partnership', sole_trader: 'Sole trader', club: 'Club',
};

const Dot = ({ color }: { readonly color: string | undefined }) => (
  <span
    className={cn('size-2.5 shrink-0 rounded-full', !color && 'bg-primary')}
    style={color ? { backgroundColor: color } : undefined}
    aria-hidden
  />
);

/** Switch between the entities (sets of books) the user belongs to, or the consolidated view. */
export const EntitySwitcher = ({ collapsed }: { readonly collapsed?: boolean }) => {
  const { memberships, entity, consolidated } = useSession();
  const setEntity = useAppStore((s) => s.setEntity);
  const set = useAppStore((s) => s.set);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const label = consolidated ? 'All entities' : entity?.name ?? 'No entity';
  const [creating, setCreating] = useState(false);

  const choose = (id: string) => {
    setEntity(id);
    void qc.invalidateQueries();
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-tour="entity-switcher"
          aria-label={collapsed ? `Current entity: ${label}. Switch entity` : undefined}
          className={cn(
            'flex min-h-11 w-full items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-left text-sm hover:bg-surface-sunken',
            collapsed && 'justify-center px-0',
          )}
        >
          {consolidated ? <Layers className="size-4 text-primary" aria-hidden /> : <Dot color={entity?.color} />}
          {collapsed ? null : (
            <>
              <span className="min-w-0 flex-1">
                <span className="sr-only">Current entity: </span>
                <span className="block truncate font-medium text-text">{label}</span>
                {entity && !consolidated ? <span className="block truncate text-xs text-text-muted">{ENTITY_TYPE_LABEL[entity.type]}</span> : null}
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-text-muted" aria-hidden />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Entities</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem key={m.entityId} onSelect={() => choose(m.entityId)}>
            <Dot color={m.entity.color} />
            <span className="min-w-0 flex-1 truncate">{m.entity.name}</span>
            <span className="text-xs text-text-muted">{ENTITY_TYPE_LABEL[m.entity.type]}</span>
            {!consolidated && m.entityId === entity?.id ? <Check className="!text-primary" aria-label="Current" /> : null}
          </DropdownMenuItem>
        ))}
        {memberships.length > 1 ? (
          <DropdownMenuItem onSelect={() => set({ consolidated: true })}>
            <Layers aria-hidden />
            <span className="flex-1">Consolidated</span>
            {consolidated ? <Check className="!text-primary" aria-label="Current" /> : null}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setCreating(true)}>
          <Plus aria-hidden />
          Create new entity
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <NewEntityDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
};
